import { ChangeEvent, useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { gameService } from "services/game/game.service"
import {
  CategoryCreateInput,
  CategoryRecord,
  CategoryUpdateInput,
  PLAYER_LOCALES,
  PlayerLocale
} from "services/game/type"

import { slugify } from "./helpers/category-slug"

export type CategoryFormState = { kind: "none" } | { kind: "create" } | { kind: "edit"; record: CategoryRecord }

interface CategoryFormModalProps {
  state: CategoryFormState
  onClose: () => void
  onSaved: () => void
}

const ICON_OPTIONS: { value: string | null; label: string }[] = [
  { value: null, label: "— (none)" },
  { value: "slots", label: "slots" },
  { value: "live", label: "live" },
  { value: "shows", label: "shows" },
  { value: "originals", label: "originals" },
  { value: "new", label: "new" },
  { value: "recent", label: "recent" },
  { value: "favourite", label: "favourite" }
]

export const CategoryFormModal = ({ state, onClose, onSaved }: CategoryFormModalProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const isEdit = state.kind === "edit"
  const open = state.kind !== "none"

  const [key, setKey] = useState("")
  const [titles, setTitles] = useState<Partial<Record<PlayerLocale, string>>>({})
  const [iconKey, setIconKey] = useState<string | null>(null)
  const [enabled, setEnabled] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (state.kind === "create") {
      setKey("")
      setTitles({})
      setIconKey(null)
      setEnabled(true)
    } else if (state.kind === "edit") {
      const r = state.record
      setKey(r.key)
      setTitles((r.title as Partial<Record<PlayerLocale, string>>) ?? {})
      setIconKey(r.icon_key)
      setEnabled(r.enabled)
    }
    setSaving(false)
  }, [state])

  if (!open) return null

  // Build the title payload: only include non-blank entries. `en` is the
  // backend's required minimum and the FE's fallback when a user's locale
  // is missing — every other locale is optional.
  const title: Partial<Record<PlayerLocale, string>> = {}
  for (const loc of PLAYER_LOCALES) {
    const v = titles[loc]?.trim()
    if (v) title[loc] = v
  }

  const titleEnFilled = (titles.en ?? "").trim().length > 0

  // Shown so the form can say where the category will live. The slug itself is
  // edited in CMS Pages; on create the backend derives it from the English title.
  const effectiveSlug = state.kind === "edit" ? state.record.slug : slugify(titles.en ?? "") || slugify(key)

  const canSave = (isEdit ? titleEnFilled : key.trim().length > 0 && titleEnFilled)

  const handleSubmit = async () => {
    if (!canSave || saving) return
    setSaving(true)
    if (state.kind === "create") {
      const input: CategoryCreateInput = {
        key: key.trim(),
        title,
        icon_key: iconKey,
        enabled
      }
      const created = await gameService.createCategory(input)
      setSaving(false)
      if (created) {
        toast.success(
          String(t("gameManagement.categoryForm.createdToast", { defaultValue: `Created category "{{key}}"`, key: created.key }))
        )
        onSaved()
      }
    } else if (state.kind === "edit") {
      const patch: CategoryUpdateInput = {
        title,
        icon_key: iconKey,
        enabled
      }
      const updated = await gameService.updateCategory(state.record.key, patch)
      setSaving(false)
      if (updated) {
        toast.success(String(t("gameManagement.categoryForm.updatedToast", { defaultValue: "Category updated" })))
        onSaved()
      }
    }
  }

  return (
    <Modal open={open} handler={onClose}>
      <div className="modal-box w-[min(560px,95vw)] text-[rgba(6,7,21,1)] dark:text-white">
        <header className="mb-4">
          <h3 className="font-bold text-lg">
            {isEdit
              ? t("gameManagement.categoryForm.editTitle", {
                  defaultValue: `Edit "{{key}}"`,
                  key: state.kind === "edit" ? state.record.key : ""
                })
              : t("gameManagement.categoryForm.createTitle", { defaultValue: "New manual category" })}
          </h3>
        </header>

        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="opacity-70">
              {t("gameManagement.categoryForm.keyLabel", { defaultValue: "Key" })}{" "}
              {!isEdit && (
                <span className="text-xs">
                  {t("gameManagement.categoryForm.keyHint", {
                    defaultValue: "(2-64 chars, lowercase letters / digits / -, _)"
                  })}
                </span>
              )}
            </span>
            <input
              type="text"
              className="input input-bordered bg-white text-black border-black/10 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10 font-mono disabled:opacity-60"
              value={key}
              disabled={isEdit}
              placeholder={t("gameManagement.categoryForm.keyPlaceholder", { defaultValue: "e.g. halloween-2026" })}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setKey(e.target.value.toLowerCase())}
            />
          </label>

          {/* URL and SEO live in CMS Pages → Game categories: one place answers
              "what is the title of this URL" for every route on the site. */}
          {isEdit && (
            <p className="rounded border border-black/10 p-3 text-xs opacity-70 dark:border-white/10">
              {t("gameManagement.categoryForm.seoMoved", {
                defaultValue:
                  "URL slug and search metadata for this category are managed in CMS Pages → Game categories.",
                slug: effectiveSlug
              })}{" "}
              <span className="font-mono break-all">/casino/{effectiveSlug}</span>
            </p>
          )}

          <div className="flex flex-col gap-1">
            <span className="text-sm opacity-70">
              {t("gameManagement.categoryForm.titlesLabel", { defaultValue: "Titles per language" })}{" "}
              <span className="text-xs">
                {t("gameManagement.categoryForm.titlesHint", {
                  defaultValue: "(en is required; player FE falls back to it when the user's locale is missing)"
                })}
              </span>
            </span>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {PLAYER_LOCALES.map((loc) => (
                <label key={loc} className="flex flex-col gap-1 text-sm">
                  <span className="opacity-70">
                    {loc}
                    {loc === "en" && " *"}
                  </span>
                  <input
                    type="text"
                    className="input input-bordered bg-white text-black border-black/10 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10"
                    value={titles[loc] ?? ""}
                    onChange={(e) => setTitles({ ...titles, [loc]: e.target.value })}
                  />
                </label>
              ))}
            </div>
          </div>

          <label className="flex flex-col gap-1 text-sm">
            <span className="opacity-70">{t("gameManagement.categoryForm.iconLabel", { defaultValue: "Icon" })}</span>
            <select
              className="select select-bordered bg-white text-black border-black/10 dark:bg-[#0C0C1A] dark:text-white dark:border-white/10"
              value={iconKey ?? ""}
              onChange={(e) => setIconKey(e.target.value || null)}
            >
              {ICON_OPTIONS.map((o) => (
                <option key={o.value ?? "_none"} value={o.value ?? ""}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className="text-xs opacity-60">
              {t("gameManagement.categoryForm.iconHint", {
                defaultValue: "Reuses an existing icon. Custom icons can be added via the FE's icon registry."
              })}
            </span>
          </label>

          <div className="flex gap-4 mt-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="toggle toggle-sm" checked={enabled} onChange={() => setEnabled((v) => !v)} />
              <span>{t("gameManagement.categoryForm.enabledLabel", { defaultValue: "Enabled (global)" })}</span>
            </label>
          </div>
        </div>

        <div className="modal-action gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSave || saving}
            className={clsx(
              "btn btn-sm bg-[rgba(132,_0,_168,_1)] hover:bg-[rgba(169,_78,_206,_1)] text-white border-transparent disabled:opacity-40"
            )}
          >
            {saving
              ? t("gameManagement.common.saving", { defaultValue: "Saving…" })
              : isEdit
              ? t("gameManagement.common.saveChanges", { defaultValue: "Save changes" })
              : t("gameManagement.categoryForm.createCta", { defaultValue: "Create" })}
          </button>
        </div>
      </div>
    </Modal>
  )
}
